/* eslint-disable jsx-a11y/anchor-has-content, jsx-a11y/anchor-is-valid */
import _ from 'underscore';
import React, { useContext, useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Link, ChoiceGroup, Dropdown, TextField } from '@fluentui/react';
import classNames from 'classnames';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';
import { ConversionHelpContext } from '@bingads-webui-campaign-react/conversion-help';
import { GOAL_REVENUE_VALUE_MAX } from '@bingads-webui-campaign-react/conversion-utils';
import { useIsUetTag } from '@bingads-webui-campaign-react/conversion-data';

import { getRevenueTypeSelectorStyles } from './goal-revenue-form-styles';

const validateDecimal = (value) => {
  const regex = /^\d+(\.\d{1,3})?$/;
  return String(value).match(regex);
};

export const RevenueTypeSelector = (props) => {
  const {
    revenue,
    revenueType,
    currency,
    onRevenueChange,
    onRevenueTypeChange,
    onCurrencyChange,
    currencies,
    onNotifyValidationResult,
  } = props;
  const i18n = useContext(I18nContext);
  const helpIds = useContext(ConversionHelpContext);
  const classes = useDefaultStyles(getRevenueTypeSelectorStyles);
  const isUetTag = useIsUetTag();
  const [isInitialized, setIsInitialized] = useState(!_.isEmpty(revenue));

  const getValueErrorMessage = useCallback((value) => {
    if (_.isUndefined(value) || value.length === 0) {
      return i18n.getString(_TL_('Enter a revenue amount'));
    } else if (!validateDecimal(value)) {
      return i18n.getString(_TL_('Enter a valid revenue amount'));
    }
    const number = parseFloat(value);
    if (number < 0 || number > GOAL_REVENUE_VALUE_MAX) {
      return i18n.getString(_TL_('Revenue amount is out of range, min 0 and max 999999999999.999'));
    }
    return undefined;
  }, [i18n]);

  const handleRevenueChange = useCallback((ev) => {
    setIsInitialized(true);
    const { target: { value } = {} } = ev || {};
    return onRevenueChange(value);
  }, [onRevenueChange]);

  const valueInput = useMemo(() => (
    <div>
      <div className={classNames('value-container', classes.valueContainer)}>
        <TextField
          className="revenue-input"
          type="number"
          min={0}
          max={GOAL_REVENUE_VALUE_MAX}
          step={0.001}
          onGetErrorMessage={getValueErrorMessage}
          onNotifyValidationResult={onNotifyValidationResult}
          ariaLabel={i18n.getString(_TL_('Revenue value'))}
          value={revenue}
          onChange={handleRevenueChange}
          styles={{ root: { marginRight: '8px' } }}
          validateOnLoad={isInitialized}
        />
        <Dropdown
          className="currency-dropdown"
          selectedKey={currency}
          options={currencies}
          onChange={(event, option) => onCurrencyChange(option.key)}
          dropdownWidth="auto"
          ariaLabel={i18n.getString(_TL_('Select revenue currency'))}
        />
      </div>
    </div>
  ), [
    classes,
    getValueErrorMessage,
    onNotifyValidationResult,
    i18n,
    revenue,
    currency,
    currencies,
    handleRevenueChange,
    onCurrencyChange,
    isInitialized,
  ]);

  const options = useMemo(() => {
    const optionStyles = {
      root: {
        fontWeight: 600,
        fontSize: '14px',
        padding: '16px 0',
        margin: 0,
      },
    };

    const variantValueDescription = i18n.getString(isUetTag ?
      _TL_('Enter the default amount and select the default currency to be used when no value is received for a conversion. The revenue value will change based on the customization you make to the UET tag tracking code that you add to your website. {{help}}Learn more{{/help}} | {{examplesLink}}See examples{{/examplesLink}}') :
      _TL_('Enter the default amount and select the default currency to be used when no value is received for a conversion. You will need to update your website code so that the revenue value gets sent to Microsoft Advertising as part of a custom event when the conversion happens. {{help}}Learn more{{/help}} | {{examplesLink}}See examples{{/examplesLink}}'));

    return ([
      {
        key: 'FixedValue',
        text: i18n.getString(_TL_('Each conversion action has the same value')),
        onRenderField: (fieldProps, render) => {
          const { checked } = fieldProps;
          return (
            <div>
              {render(fieldProps)}
              {checked &&
                <div className={classNames('fixed-value-option', classes.valueOption)}>
                  <p className={classNames('description', classes.description)}>{i18n.getString(_TL_('Each time it happens, the conversion action has the same value. Enter the amount and select the currency. This is a static revenue value that doesn\'t change.'))}</p>
                  {valueInput}
                </div>
              }
            </div>
          );
        },
        styles: optionStyles,
      },
      {
        key: 'VariantValue',
        text: i18n.getString(_TL_('Conversion action value may vary (i.e. by purchase price)')),
        onRenderField: (fieldProps, render) => {
          const { checked } = fieldProps;
          return (
            <div>
              {render(fieldProps)}
              {checked &&
                <div className={classNames('variant-value-option', classes.valueOption)}>
                  <div className={classNames('description', classes.description)}>
                    <ReactTemplate
                      model={{
                        examplesLink: text => (
                          <Link
                            className="link examples-link"
                            href="https://go.microsoft.com/fwlink/?linkid=717484"
                            target="_blank"
                            aria-label={text}
                            role="link"
                          >
                            {text}
                          </Link>),
                        help: text => <LearnMoreLink topic={helpIds.revenueVariantValueLearnMore} linkText={text} />,
                      }}
                      template={variantValueDescription}
                    />
                  </div>
                  {valueInput}
                </div>
              }
            </div>
          );
        },
        styles: optionStyles,
      },
      { key: 'NoValue', text: i18n.getString(_TL_('No value (Not recommended)')), styles: optionStyles },
    ]);
  }, [helpIds, i18n, classes, valueInput, isUetTag]);

  const onChange = useCallback((ev, selection) => {
    onRevenueTypeChange(selection.key);
    if (selection.key === 'NoValue' && _.isFunction(onNotifyValidationResult)) {
      // if NoValue is selected, the form is valid
      onNotifyValidationResult('');
    }
  }, [onNotifyValidationResult, onRevenueTypeChange]);
  return (
    <div className={classNames('revenue-type-selector', classes.container)}>
      <ChoiceGroup options={options} selectedKey={revenueType} onChange={onChange} className={classes.choiceGroup} />
    </div>);
};


RevenueTypeSelector.propTypes = {
  revenue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  revenueType: PropTypes.oneOf(['FixedValue', 'VariantValue', 'NoValue']),
  currency: PropTypes.string.isRequired,
  onRevenueChange: PropTypes.func.isRequired,
  onRevenueTypeChange: PropTypes.func.isRequired,
  onCurrencyChange: PropTypes.func.isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onNotifyValidationResult: PropTypes.func,
};
RevenueTypeSelector.defaultProps = {
  revenue: '',
  revenueType: undefined,
  onNotifyValidationResult: null,
};
